import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  getDocs,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { FirestoreTeam, TeamMember, TeamRole, FirestoreGame, FirestoreSeason } from '../types/auth';

// Firestoreは undefined を保存できないため、deepに除去する
const sanitizeForFirestore = <T,>(value: T): T => {
  if (value === undefined) return undefined as unknown as T;
  if (value === null) return value;
  if (Array.isArray(value)) {
    return value.map((v) => sanitizeForFirestore(v)) as unknown as T;
  }
  if (typeof value === 'object') {
    const obj: any = value;
    // TimestampなどFirestoreの特殊オブジェクトはそのまま
    if (obj instanceof Timestamp) return value;
    const out: any = {};
    Object.keys(obj).forEach((k) => {
      const v = obj[k];
      if (v === undefined) return;
      out[k] = sanitizeForFirestore(v);
    });
    return out as T;
  }
  return value;
};

// 招待コードを生成（6桁の英数字）
export const generateInviteCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// チームを作成（タイムアウト付き）
export const createTeam = async (
  teamName: string,
  ownerId: string,
  ownerEmail: string
): Promise<string> => {
  try {
    console.log('[Firestore] Creating team:', teamName);
    
    const teamId = doc(collection(db, 'teams')).id;
    const inviteCode = generateInviteCode();
    
    const team: FirestoreTeam = {
      id: teamId,
      name: teamName,
      ownerId,
      inviteCode,
      members: [
        {
          uid: ownerId,
          email: ownerEmail,
          displayName: ownerEmail.split('@')[0],
          role: 'owner',
          joinedAt: new Date().toISOString(),
        },
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      settings: {
        allowGuestView: false,
        requireApproval: false,
      },
    };

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Timeout')), 10000);
    });
    
    const createPromise = setDoc(doc(db, 'teams', teamId), team);
    
    await Promise.race([createPromise, timeoutPromise]);
    
    console.log('[Firestore] Team created:', teamId);
    return teamId;
  } catch (error) {
    console.error('[Firestore] Failed to create team:', error);
    throw error;
  }
};

// チームを取得
export const getTeam = async (teamId: string): Promise<FirestoreTeam | null> => {
  const teamDoc = await getDoc(doc(db, 'teams', teamId));
  
  if (!teamDoc.exists()) {
    return null;
  }
  
  return teamDoc.data() as FirestoreTeam;
};

// チームを更新（players/abbreviation/logoUrl等）
export const updateTeam = async (
  teamId: string,
  updates: Partial<Omit<FirestoreTeam, 'id' | 'createdAt'>>
): Promise<void> => {
  try {
    console.log('[Firestore] Updating team:', teamId);
    const teamRef = doc(db, 'teams', teamId);
    const sanitized = sanitizeForFirestore({
      ...updates,
      updatedAt: new Date().toISOString(),
    });
    await updateDoc(teamRef, sanitized as any);
    console.log('[Firestore] Team updated:', teamId);
  } catch (error) {
    console.error('[Firestore] Failed to update team:', error);
    throw error;
  }
};

// 招待コードでチームを検索
export const findTeamByInviteCode = async (
  inviteCode: string
): Promise<FirestoreTeam | null> => {
  const q = query(
    collection(db, 'teams'),
    where('inviteCode', '==', inviteCode.toUpperCase())
  );
  
  const querySnapshot = await getDocs(q);
  
  if (querySnapshot.empty) {
    return null;
  }
  
  return querySnapshot.docs[0].data() as FirestoreTeam;
};

// ユーザーのチーム一覧を取得（タイムアウト付き）
export const getUserTeams = async (userId: string): Promise<FirestoreTeam[]> => {
  try {
    console.log('[Firestore] Getting user teams for:', userId);
    
    // タイムアウトを設定（10秒）
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Timeout')), 10000);
    });
    
    const getTeamsPromise = (async () => {
      const teamsSnapshot = await getDocs(collection(db, 'teams'));
      
      const userTeams: FirestoreTeam[] = [];
      
      teamsSnapshot.forEach((doc) => {
        const team = doc.data() as FirestoreTeam;
        
        if (team.members && team.members.some((m) => m.uid === userId)) {
          userTeams.push(team);
        }
      });
      
      console.log('[Firestore] Found teams:', userTeams.length);
      return userTeams;
    })();
    
    return await Promise.race([getTeamsPromise, timeoutPromise]);
  } catch (error: any) {
    console.error('[Firestore] Failed to get user teams:', error);
    
    // タイムアウトやAbortErrorの場合は空配列を返す
    if (error.message === 'Timeout' || error.name === 'AbortError') {
      console.log('[Firestore] Timeout or aborted, returning empty array');
      return [];
    }
    
    throw error;
  }
};

// チームにメンバーを追加
export const addTeamMember = async (
  teamId: string,
  member: TeamMember
): Promise<void> => {
  const teamRef = doc(db, 'teams', teamId);
  const teamDoc = await getDoc(teamRef);
  
  if (!teamDoc.exists()) {
    throw new Error('Team not found');
  }
  
  const team = teamDoc.data() as FirestoreTeam;
  
  // 既に参加しているか確認
  if (team.members.some((m) => m.uid === member.uid)) {
    throw new Error('Already a member');
  }
  
  await updateDoc(teamRef, {
    members: [...team.members, member],
    updatedAt: new Date().toISOString(),
  });
};

// メンバーの役割を変更
export const updateMemberRole = async (
  teamId: string,
  memberId: string,
  newRole: TeamRole
): Promise<void> => {
  const teamRef = doc(db, 'teams', teamId);
  const teamDoc = await getDoc(teamRef);
  
  if (!teamDoc.exists()) {
    throw new Error('Team not found');
  }
  
  const team = teamDoc.data() as FirestoreTeam;
  
  const updatedMembers = team.members.map((m) =>
    m.uid === memberId ? { ...m, role: newRole } : m
  );
  
  await updateDoc(teamRef, {
    members: updatedMembers,
    updatedAt: new Date().toISOString(),
  });
};

// メンバーを削除
export const removeMember = async (
  teamId: string,
  memberId: string
): Promise<void> => {
  const teamRef = doc(db, 'teams', teamId);
  const teamDoc = await getDoc(teamRef);
  
  if (!teamDoc.exists()) {
    throw new Error('Team not found');
  }
  
  const team = teamDoc.data() as FirestoreTeam;
  
  // オーナーは削除できない
  if (team.ownerId === memberId) {
    throw new Error('Cannot remove owner');
  }
  
  const updatedMembers = team.members.filter((m) => m.uid !== memberId);
  
  await updateDoc(teamRef, {
    members: updatedMembers,
    updatedAt: new Date().toISOString(),
  });
};

// 新しい招待コードを生成
export const regenerateInviteCode = async (teamId: string): Promise<string> => {
  const newCode = generateInviteCode();
  
  await updateDoc(doc(db, 'teams', teamId), {
    inviteCode: newCode,
    updatedAt: new Date().toISOString(),
  });
  
  return newCode;
};

// ============================================
// ゲーム関連の関数
// ============================================

// ゲームを作成
export const createGame = async (
  teamId: string,
  gameData: Omit<FirestoreGame, 'id' | 'teamId' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  try {
    console.log('[Firestore] Creating game:', gameData);
    
    const gameId = doc(collection(db, 'games')).id;
    
    const game: FirestoreGame = {
      ...gameData,
      id: gameId,
      teamId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await setDoc(doc(db, 'games', gameId), game);
    
    console.log('[Firestore] Game created:', gameId);
    return gameId;
  } catch (error) {
    console.error('[Firestore] Failed to create game:', error);
    throw error;
  }
};

// ゲームを取得
export const getGame = async (gameId: string): Promise<FirestoreGame | null> => {
  try {
    const gameDoc = await getDoc(doc(db, 'games', gameId));
    
    if (!gameDoc.exists()) {
      return null;
    }
    
    return gameDoc.data() as FirestoreGame;
  } catch (error) {
    console.error('[Firestore] Failed to get game:', error);
    return null;
  }
};

// チームの全ゲームを取得
export const getTeamGames = async (teamId: string): Promise<FirestoreGame[]> => {
  try {
    console.log('[Firestore] Getting games for team:', teamId);
    
    const q = query(
      collection(db, 'games'),
      where('teamId', '==', teamId)
    );
    
    const querySnapshot = await getDocs(q);
    
    const games: FirestoreGame[] = [];
    querySnapshot.forEach((doc) => {
      games.push(doc.data() as FirestoreGame);
    });
    
    console.log('[Firestore] Found games:', games.length);
    return games;
  } catch (error: any) {
    console.error('[Firestore] Failed to get team games:', error);
    
    // エラーが発生しても空配列を返す
    return [];
  }
};

// ゲームを更新
export const updateGame = async (
  gameId: string,
  updates: Partial<Omit<FirestoreGame, 'id' | 'createdAt'>>
): Promise<void> => {
  try {
    console.log('[Firestore] Updating game:', gameId);
    
    const gameRef = doc(db, 'games', gameId);
    
    const sanitized = sanitizeForFirestore({
      ...updates,
      updatedAt: new Date().toISOString(),
    });

    await updateDoc(gameRef, sanitized as any);
    
    console.log('[Firestore] Game updated:', gameId);
  } catch (error) {
    console.error('[Firestore] Failed to update game:', error);
    throw error;
  }
};

// ゲームを削除
export const deleteGame = async (gameId: string): Promise<void> => {
  try {
    console.log('[Firestore] Deleting game:', gameId);
    
    await deleteDoc(doc(db, 'games', gameId));
    
    console.log('[Firestore] Game deleted:', gameId);
  } catch (error) {
    console.error('[Firestore] Failed to delete game:', error);
    throw error;
  }
};

// ============================================
// シーズン関連の関数
// ============================================

// シーズンを作成
export const createSeason = async (
  teamId: string,
  seasonData: Omit<FirestoreSeason, 'id' | 'teamId' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  try {
    console.log('[Firestore] Creating season:', seasonData);
    
    const seasonId = doc(collection(db, 'seasons')).id;
    
    const season: FirestoreSeason = {
      ...seasonData,
      id: seasonId,
      teamId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await setDoc(doc(db, 'seasons', seasonId), season);
    
    console.log('[Firestore] Season created:', seasonId);
    return seasonId;
  } catch (error) {
    console.error('[Firestore] Failed to create season:', error);
    throw error;
  }
};

// シーズンを取得
export const getSeason = async (seasonId: string): Promise<FirestoreSeason | null> => {
  try {
    const seasonDoc = await getDoc(doc(db, 'seasons', seasonId));
    
    if (!seasonDoc.exists()) {
      return null;
    }
    
    return seasonDoc.data() as FirestoreSeason;
  } catch (error) {
    console.error('[Firestore] Failed to get season:', error);
    return null;
  }
};

// チームの全シーズンを取得
export const getTeamSeasons = async (teamId: string): Promise<FirestoreSeason[]> => {
  try {
    console.log('[Firestore] Getting seasons for team:', teamId);
    
    const q = query(
      collection(db, 'seasons'),
      where('teamId', '==', teamId)
    );
    
    const querySnapshot = await getDocs(q);
    
    const seasons: FirestoreSeason[] = [];
    querySnapshot.forEach((doc) => {
      seasons.push(doc.data() as FirestoreSeason);
    });
    
    console.log('[Firestore] Found seasons:', seasons.length);
    return seasons;
  } catch (error) {
    console.error('[Firestore] Failed to get team seasons:', error);
    return [];
  }
};

// シーズンを更新
export const updateSeason = async (
  seasonId: string,
  updates: Partial<Omit<FirestoreSeason, 'id' | 'teamId' | 'createdAt'>>
): Promise<void> => {
  try {
    console.log('[Firestore] Updating season:', seasonId);
    
    const seasonRef = doc(db, 'seasons', seasonId);
    
    await updateDoc(seasonRef, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
    
    console.log('[Firestore] Season updated:', seasonId);
  } catch (error) {
    console.error('[Firestore] Failed to update season:', error);
    throw error;
  }
};

// シーズンを削除
export const deleteSeason = async (seasonId: string): Promise<void> => {
  try {
    console.log('[Firestore] Deleting season:', seasonId);
    
    await deleteDoc(doc(db, 'seasons', seasonId));
    
    console.log('[Firestore] Season deleted:', seasonId);
  } catch (error) {
    console.error('[Firestore] Failed to delete season:', error);
    throw error;
  }
};

// ============================================
// リアルタイムリスナー関連の関数
// ============================================

/**
 * チームのゲームをリアルタイムで監視
 * @param teamId チームID
 * @param callback 更新時のコールバック
 * @returns Unsubscribe関数
 */
export const subscribeToTeamGames = (
  teamId: string,
  callback: (games: FirestoreGame[]) => void,
  seasonId?: string
): Unsubscribe => {
  console.log('[Firestore] Subscribing to team games:', teamId, seasonId ? `(season: ${seasonId})` : '(all seasons)');
  
  // シーズンIDが指定されている場合はフィルタリング
  const q = seasonId
    ? query(
        collection(db, 'games'),
        where('teamId', '==', teamId),
        where('seasonId', '==', seasonId)
      )
    : query(
        collection(db, 'games'),
        where('teamId', '==', teamId)
      );
  
  const unsubscribe = onSnapshot(
    q,
    (querySnapshot) => {
      const games: FirestoreGame[] = [];
      querySnapshot.forEach((doc) => {
        games.push({ ...doc.data(), id: doc.id } as FirestoreGame);
      });
      
      console.log('[Firestore] Games updated (realtime):', games.length, seasonId ? `for season ${seasonId}` : '');
      callback(games);
    },
    (error) => {
      // AbortError は無視（ページ遷移時などに発生する正常な動作）
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        console.log('[Firestore] Games listener aborted (normal during navigation)');
        return;
      }
      console.error('[Firestore] Error subscribing to games:', error);
    }
  );
  
  return unsubscribe;
};

/**
 * チームのシーズンをリアルタイムで監視
 * @param teamId チームID
 * @param callback 更新時のコールバック
 * @returns Unsubscribe関数
 */
export const subscribeToTeamSeasons = (
  teamId: string,
  callback: (seasons: FirestoreSeason[]) => void
): Unsubscribe => {
  console.log('[Firestore] Subscribing to team seasons:', teamId);
  
  const q = query(
    collection(db, 'seasons'),
    where('teamId', '==', teamId)
  );
  
  const unsubscribe = onSnapshot(
    q,
    (querySnapshot) => {
      const seasons: FirestoreSeason[] = [];
      querySnapshot.forEach((doc) => {
        seasons.push({ ...doc.data(), id: doc.id } as FirestoreSeason);
      });
      
      console.log('[Firestore] Seasons updated (realtime):', seasons.length);
      callback(seasons);
    },
    (error) => {
      // AbortError は無視（ページ遷移時などに発生する正常な動作）
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        console.log('[Firestore] Seasons listener aborted (normal during navigation)');
        return;
      }
      console.error('[Firestore] Error subscribing to seasons:', error);
    }
  );
  
  return unsubscribe;
};
